import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import FeedbackButton from './components/FeedbackButton'
import ErrorBoundary from './components/ErrorBoundary'

// C3 - Lazy loading des routes : chaque page devient son propre bundle.
// Bundle initial reduit, le routeur ne charge la page qu au moment ou
// l utilisateur y navigue. Login + Dashboard restent eager (entree principale).
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
const Search = lazy(() => import('./pages/Search'))
const CRM = lazy(() => import('./pages/CRM'))
const Agenda = lazy(() => import('./pages/Agenda'))
const Export = lazy(() => import('./pages/Export'))
const LinkedInPosts = lazy(() => import('./pages/LinkedInPosts'))
const Suppliers = lazy(() => import('./pages/Suppliers'))
const Watchlist = lazy(() => import('./pages/Watchlist'))
const Settings = lazy(() => import('./pages/Settings'))
const AdminUsers = lazy(() => import('./pages/AdminUsers'))
const MonthlyReport = lazy(() => import('./pages/MonthlyReport'))
const Notes = lazy(() => import('./pages/Notes'))
const Tour = lazy(() => import('./pages/Tour'))
const Leads = lazy(() => import('./pages/Leads'))
const Legal = lazy(() => import('./pages/Legal'))
const AdminDeploy = lazy(() => import('./pages/AdminDeploy'))

function RouteSpinner() {
  return (
    <div className="flex items-center justify-center h-64 text-slate-400">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-600 border-t-blue-500" />
    </div>
  )
}

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token')
  const location = useLocation()
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return children
}

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === '1'
  )
  const toggleCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev
      localStorage.setItem('sidebar_collapsed', next ? '1' : '0')
      return next
    })
  }
  // Raccourci clavier Ctrl+B / Cmd+B pour toggle (standard VS Code, GitHub…)
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        // Évite de capturer dans les <input> où ⌘B = gras
        const tag = document.activeElement?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA') return
        e.preventDefault()
        toggleCollapsed()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])
  return (
    <div className="flex h-screen overflow-hidden bg-dark-950">
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleCollapsed}
      />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden w-full">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          <ErrorBoundary>
          <Suspense fallback={<RouteSpinner />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/search" element={<Search />} />
            <Route path="/crm" element={<CRM />} />
            <Route path="/contacts-linkedin" element={<Leads />} />
            <Route path="/agenda" element={<Agenda />} />
            <Route path="/tournee" element={<Tour />} />
            <Route path="/report" element={<MonthlyReport />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/clients-bydemes" element={<CRM />} />
            <Route path="/export" element={<Export />} />
            <Route path="/linkedin" element={<LinkedInPosts />} />
            <Route path="/fournisseurs" element={<Suppliers />} />
            <Route path="/veille-linkedin" element={<Watchlist />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admin-users" element={<AdminUsers />} />
            <Route path="/admin-deploy" element={<AdminDeploy />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <FeedbackButton />
    </div>
  )
}

export default function App() {
  return (
    <Suspense fallback={<RouteSpinner />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/legal" element={<Legal />} />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <AppLayout />
            </PrivateRoute>
          }
        />
      </Routes>
    </Suspense>
  )
}
